/**
 * Display rules for a claimed coupon's verified summary (see api/coupons.js). Pure functions,
 * ported from parabo-swift (CouponSummary+Display, StoreProduct+Coupon) so both clients promise
 * the same thing. Advisory only: the order price quote remains the authority on the money.
 */
import { formatPrice } from 'utils/format';
import { getSkuProductPrices } from 'data/lib/product';

const toCents = (amount) => Math.round(amount * 100);

const percentageDiscountCents = (cents, value) => Math.min(cents, Math.round((cents * value) / 100));

// Backend Product.giftcard_id?: gift cards never take a coupon discount and never count toward
// a coupon minimum, whatever the coupon's restriction says.
const GIFT_CARD_PREFIX = 'giftcard-';

export const isGiftCardProductId = (productId) =>
  typeof productId === 'string' && productId.startsWith(GIFT_CARD_PREFIX);

export const couponAppliesTo = (summary, productId) =>
  !isGiftCardProductId(productId) &&
  (summary.productIds == null || summary.productIds.includes(productId));

// Whether anything in the cart can take the coupon. True while the cart is empty or the coupon
// is not verified, so the cart never claims "nothing qualifies" without evidence. False for a
// product-restricted coupon covering nothing in the cart and for a gift-card-only cart.
export const couponCoversCart = (summary, cartProductIds) =>
  !summary ||
  cartProductIds.length === 0 ||
  cartProductIds.some((id) => couponAppliesTo(summary, id));

// A coupon whose only effect is free shipping has nothing to show on a price.
export const isFreeShippingOnly = (summary) =>
  summary.value === 0 && summary.freeShippingTypes.length > 0;

/**
 * Catalog price after a percentage coupon, rounded half-up to cents like the backend's
 * Money.from_amount. Null for fixed-amount coupons (they apply once per product group in the
 * cart, so no per-unit price exists), for uncovered products, and when rounding leaves no
 * reduction, so the UI never strikes out an equal price.
 */
export const discountedPrice = (summary, price, productId) => {
  if (!summary.isPercentage || !couponAppliesTo(summary, productId)) {
    return null;
  }

  const cents = toCents(price);
  const discountCents = percentageDiscountCents(cents, summary.value);
  const discounted = (cents - discountCents) / 100;

  return discounted < price ? discounted : null;
};

// Unrestricted coupons round once on the total. Restricted coupons round once per product
// group, matching ProductPrice#coupon_discount even when several lines share a product id.
export const discountedPartsPrice = (summary, parts) => {
  if (!summary.isPercentage) {
    return null;
  }

  const groups = new Map();
  let totalCents = 0;
  for (const { id, price } of parts) {
    const cents = toCents(price);
    totalCents += cents;
    if (couponAppliesTo(summary, id)) {
      const group = summary.productIds == null ? null : id;
      groups.set(group, (groups.get(group) ?? 0) + cents);
    }
  }

  let discountCents = 0;
  for (const cents of groups.values()) {
    discountCents += percentageDiscountCents(cents, summary.value);
  }
  return discountCents > 0 ? (totalCents - discountCents) / 100 : null;
};

// "25%" / "12.5%"
const formatPercent = (value) => `${Number(value.toFixed(1))}%`;

const formatList = (names) => new Intl.ListFormat('en-US', { type: 'conjunction' }).format(names);

// "25% off", "$5.00 off", or "Free shipping".
export const discountLabel = (summary) => {
  if (isFreeShippingOnly(summary)) {
    return 'Free shipping';
  }
  if (summary.isPercentage) {
    return `${formatPercent(summary.value)} off`;
  }
  return `${formatPrice(summary.value, summary.currency)} off`;
};

/**
 * The offer in one line: "25% off everything", "25% off Photo Books and Prints", or
 * "25% off selected products" when the restriction names nothing merchandised (bundle-only)
 * or too much to list. `productNames` are the display names of the covered products.
 */
export const offerLabel = (summary, productNames = []) => {
  const label = discountLabel(summary);

  if (isFreeShippingOnly(summary)) {
    return label;
  }
  if (summary.productIds == null) {
    return `${label} everything`;
  }

  const names = [...new Set(productNames)];
  if (names.length === 0 || names.length > 3) {
    return `${label} selected products`;
  }
  return `${label} ${formatList(names)}`;
};

// The order-level condition stays visible while browsing: one product's price never
// establishes coupon eligibility.
export const minimumOrderLabel = (summary, productNames = []) =>
  `${offerLabel(summary, productNames)} on orders of ${formatPrice(
    summary.requiredMinimumValue,
    summary.currency,
  )} or more`;

// "25% off with code LABOR, applied at checkout"
export const checkoutLabel = (summary) =>
  `${discountLabel(summary)} with code ${summary.code}, applied at checkout`;

// A deadline is worth naming only when it is close enough to matter.
const ENDS_SOON_DAYS = 14;

const DAY_MS = 24 * 60 * 60 * 1000;

// The backend's calendar day: it compares `ends_at` with `Date.today` on a UTC server, so a
// coupon expires at midnight UTC, not at the shopper's local midnight.
export const utcDay = (now = new Date()) => now.toISOString().slice(0, 10);

// "Ends Mon, Sep 7" while the coupon's last valid day is within ENDS_SOON_DAYS, null otherwise
// (open-ended, far away, or already over). Both days are read at UTC noon so the label flips to
// null at the moment the backend starts rejecting the code.
export const endsSoonLabel = (summary, now = new Date()) => {
  if (!summary.endsAt) {
    return null;
  }
  const endsAt = new Date(`${summary.endsAt}T12:00:00Z`);
  const today = new Date(`${utcDay(now)}T12:00:00Z`);
  const daysLeft = Math.round((endsAt - today) / DAY_MS);
  if (Number.isNaN(daysLeft) || daysLeft < 0 || daysLeft > ENDS_SOON_DAYS) {
    return null;
  }
  const day = new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(endsAt);
  return daysLeft === 0 ? 'Ends today' : `Ends ${day}`;
};

// What a verified claim is worth in one line: the offer, or the minimum-order condition while
// the coupon has one (the cart's Discount row says whether it was met).
export const claimLabel = (summary, productNames = []) =>
  summary.requiredMinimumValue > 0
    ? minimumOrderLabel(summary, productNames)
    : offerLabel(summary, productNames);

const canPreviewProduct = (summary, productId) =>
  !!summary && !!productId && !isFreeShippingOnly(summary) && couponAppliesTo(summary, productId);

/**
 * The checkout promise for a covered product, with a close end date appended.
 * The provider passes null until any order-minimum condition is confirmed by the cart quote.
 */
export const productCouponLine = (previewSummary, productId, now = new Date()) => {
  if (!canPreviewProduct(previewSummary, productId)) {
    return null;
  }
  const line = checkoutLabel(previewSummary);
  const ends = endsSoonLabel(previewSummary, now);
  return ends ? `${line} · ${ends}` : line;
};

/**
 * A PDP selection and its upsell are priced together, using their own cart product ids.
 * Coupons covering only the upsell do not advertise a discount on the primary product.
 * Fixed-amount coupons get a checkout promise without a per-unit discounted price.
 */
export const couponPricePreview = (previewSummary, configuration, quantity = 1, now = new Date()) => {
  const pricedId = configuration.bundle?.id ?? configuration.product?.id;
  if (!canPreviewProduct(previewSummary, pricedId)) {
    return { line: null, price: null };
  }

  let price = null;
  if (configuration.price != null) {
    const upsellPrice = configuration.upsell?.price ?? 0;
    const parts = [
      { id: pricedId, price: (configuration.price - upsellPrice) * quantity },
      ...(configuration.upsell ? [{ id: configuration.upsell.id, price: upsellPrice * quantity }] : []),
    ];
    price = discountedPartsPrice(previewSummary, parts);
  }
  return { line: productCouponLine(previewSummary, pricedId, now), price };
};

/**
 * A product card's sale treatment, or null when the coupon covers none of the product's SKUs
 * and bundles or only frees shipping. `badge` is the uppercase discount ("$5.00 OFF").
 * `discountedPriceFrom` is the cheapest member after a percentage coupon, computed member by
 * member so a restriction covering only pricier members cannot understate the cheapest
 * uncovered one; null for fixed-amount coupons (no per-unit price) and when nothing beats the
 * card's "from" price.
 */
export const couponSale = (summary, product) => {
  if (!summary || isFreeShippingOnly(summary)) {
    return null;
  }

  const members = [...product.options, ...product.bundles];
  if (!members.some((member) => couponAppliesTo(summary, member.id))) {
    return null;
  }

  let discountedPriceFrom = null;
  if (summary.isPercentage) {
    const cheapest = Math.min(
      ...members.map((member) => {
        const price = Math.min(...getSkuProductPrices(member));
        return discountedPrice(summary, price, member.id) ?? price;
      }),
    );
    if (cheapest < product.prices[0]) {
      discountedPriceFrom = cheapest;
    }
  }

  return { badge: discountLabel(summary).toUpperCase(), discountedPriceFrom };
};

/**
 * `couponSale` for products sold together from their starting prices (a gift idea pairing a
 * print with a place to keep it). Each product contributes the member that is cheapest after
 * the discount, then the parts round like an order: once on the total for an unrestricted
 * coupon, once per covered product group otherwise. Gift cards and uncovered parts keep their
 * full price.
 */
export const couponPairingSale = (summary, products) => {
  if (!summary || isFreeShippingOnly(summary)) {
    return null;
  }

  const memberLists = products.map((product) => [...product.options, ...product.bundles]);
  if (!memberLists.some((members) => members.some((member) => couponAppliesTo(summary, member.id)))) {
    return null;
  }

  let discountedPriceFrom = null;
  if (summary.isPercentage) {
    const parts = memberLists.map((members) =>
      members
        .map((member) => {
          const price = Math.min(...getSkuProductPrices(member));
          return { id: member.id, price, after: discountedPrice(summary, price, member.id) ?? price };
        })
        .reduce((best, part) => (part.after < best.after ? part : best)),
    );
    const total = discountedPartsPrice(summary, parts);
    const originalCents = products.reduce((sum, product) => sum + toCents(product.prices[0]), 0);
    if (total != null && toCents(total) < originalCents) {
      discountedPriceFrom = total;
    }
  }

  return { badge: discountLabel(summary).toUpperCase(), discountedPriceFrom };
};
